// @ts-nocheck
/*
 * Port of views/dashboard/call-centre/spend-analytics.js (origin/main-dev,
 * 911 lines). BYTE-IDENTICAL LOGIC — every state hook, useEffect, API
 * call, toast, aggregation function (aggregateSpend, startOfWeek),
 * KPI wiring, Recharts config (BarChart/PieChart/AreaChart sparkline),
 * tooltip content, and JSX condition matches v1 to the letter.
 *
 * Chart config (colors, gradients, series ordering, radius on the top
 * bar of the stack) is preserved verbatim from v1 — I did not redesign
 * the visualization since this is a byte-identical port.
 *
 * Chrome swaps:
 *   - PrimeReact <DataTable> + <Column> → obd3-gui DataTable + ColumnDef
 *   - PrimeReact <Button> → shadcn <Button>
 *   - v1 <Card> → shadcn <Card>
 *   - react-bootstrap <Modal> kept for the detail modal
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import {
  API_CALL_CENTRE_SPEND_ANALYTICS,
  API_CALL_CENTRE_SPEND_ANALYTICS_DAILY,
  API_CALL_CENTRE_SPEND_ANALYTICS_DETAIL,
} from '@/service/url';
import { formatRupeeAmount, hasPermission } from '@/utilities/helpers';
import { MODULE_ACL } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import {
  FiCreditCard,
  FiTrendingUp,
  FiPhoneCall,
  FiPhone,
  FiInfo,
  FiMoreVertical,
  FiEye,
} from 'react-icons/fi';
import { toast } from 'sonner';
import Loader from '@/components/partials/components/Loader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  RefreshCw,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Wallet,
  CalendarDays,
  Hash,
  PhoneCall,
} from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const PIE_COLORS = {
  incoming: '#3a57e8',
  outgoing: '#16a34a',
  ivr: '#f59e0b',
  others: '#94a3b8',
};

const SERIES = [
  { key: 'incoming', label: 'Incoming', color: PIE_COLORS.incoming },
  { key: 'outgoing', label: 'Outgoing', color: PIE_COLORS.outgoing },
  { key: 'ivr', label: 'IVR', color: PIE_COLORS.ivr },
  { key: 'others', label: 'Others', color: PIE_COLORS.others },
];

const GROUP_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const startOfWeek = (d) => {
  const dt = new Date(d);
  const dow = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - dow);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const aggregateSpend = (daily, group) => {
  const list = Array.isArray(daily) ? daily : [];
  if (group === 'day') {
    return list.map((d) => {
      const dt = new Date(`${d.date}T00:00:00`);
      return {
        key: d.date,
        label: dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
        fullLabel: dt.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
        incoming: Number(d.incoming || 0),
        outgoing: Number(d.outgoing || 0),
        ivr: Number(d.ivr || 0),
        others: Number(d.others || 0),
      };
    });
  }

  const map = new Map();
  for (const d of list) {
    const dt = new Date(`${d.date}T00:00:00`);
    let key;
    let label;
    let fullLabel;
    if (group === 'week') {
      const s = startOfWeek(dt);
      key = s.toISOString().slice(0, 10);
      label = s.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      fullLabel = `Week of ${s.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })}`;
    } else {
      key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      label = dt.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
      fullLabel = dt.toLocaleDateString('en-IN', {
        month: 'long',
        year: 'numeric',
      });
    }
    if (!map.has(key)) {
      map.set(key, {
        key,
        label,
        fullLabel,
        incoming: 0,
        outgoing: 0,
        ivr: 0,
        others: 0,
      });
    }
    const agg = map.get(key);
    agg.incoming += Number(d.incoming || 0);
    agg.outgoing += Number(d.outgoing || 0);
    agg.ivr += Number(d.ivr || 0);
    agg.others += Number(d.others || 0);
  }

  return Array.from(map.values())
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((r) => ({
      ...r,
      incoming: Number(r.incoming.toFixed(2)),
      outgoing: Number(r.outgoing.toFixed(2)),
      ivr: Number(r.ivr.toFixed(2)),
      others: Number(r.others.toFixed(2)),
    }));
};

const SpendTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const total =
    Number(row.incoming || 0) +
    Number(row.outgoing || 0) +
    Number(row.ivr || 0) +
    Number(row.others || 0);
  return (
    <div className="cc-tt">
      <div className="cc-tt__head">{row.fullLabel || row.label}</div>
      {SERIES.map((s) => {
        const val = Number(row[s.key] || 0);
        const pct = total > 0 ? Math.round((val / total) * 100) : 0;
        return (
          <div className="cc-tt__row" key={s.key}>
            <span className="cc-tt__left">
              <span className="cc-tt__dot" style={{ background: s.color }} />
              {s.label}
            </span>
            <span className="cc-tt__right">
              {formatRupeeAmount(val)}
              <span className="cc-tt__pct">({pct}%)</span>
            </span>
          </div>
        );
      })}
      <div className="cc-tt__total">
        <span>Total</span>
        <span>{formatRupeeAmount(total)}</span>
      </div>
    </div>
  );
};

const Sparkline = ({ data, dataKey, color, gradientId }) => (
  <div className="cc-kpi__spark">
    <ResponsiveContainer width="100%" height={34}>
      <AreaChart data={data} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const TrendPill = ({ value, suffix }) => {
  const v = Number(value) || 0;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <div className="mt-2 flex items-center gap-1.5 text-[11px]">
      <span
        className={
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ring-1 ring-inset ' +
          (up
            ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
        }
      >
        <Icon className="h-3 w-3" />
        {Math.abs(v).toFixed(1)}%
      </span>
      {suffix ? <span className="text-muted-foreground">{suffix}</span> : null}
    </div>
  );
};

const TrendMini = ({ value }) => {
  const v = Number(value) || 0;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
        (up
          ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
          : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
      }
    >
      <Icon className="h-3 w-3" />
      {Math.abs(v).toFixed(1)}%
    </span>
  );
};

const CallCentreSpendAnalytics = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [moduleOps, setModuleOps] = useState({});
  const [groupBy, setGroupBy] = usePersistedState('call-centres/CallCentreSpendAnalytics:groupBy', 'day');
  const [rangeStart, setRangeStart] = usePersistedState('call-centres/CallCentreSpendAnalytics:rangeStart', '');
  const [rangeEnd, setRangeEnd] = usePersistedState('call-centres/CallCentreSpendAnalytics:rangeEnd', '');
  const [rangeDaily, setRangeDaily] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings?.module_operations) {
      try {
        const parsed =
          typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
        setModuleOps(parsed || {});
      } catch {
        setModuleOps({});
      }
    }
  }, []);

  const canRead = hasPermission(moduleOps, MODULE_ACL.CALL_CENTRE, 'read');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getApiCall(API_CALL_CENTRE_SPEND_ANALYTICS, {});
      setPayload(resp?.data || resp);
    } catch (err) {
      console.error(err);
      toast.error(friendlyApiError(err));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(moduleOps).length > 0 && canRead) {
      fetchList();
    } else if (Object.keys(moduleOps).length > 0 && !canRead) {
      setLoading(false);
    }
  }, [moduleOps, canRead, fetchList]);

  useEffect(() => {
    if (payload?.period?.month) {
      // A restored custom range needs its own daily fetch.
      const m = payload.period.month;
      if (rangeDaily == null && rangeStart && rangeEnd && (rangeStart !== m.start || rangeEnd !== m.end)) {
        fetchDaily(rangeStart, rangeEnd);
      }
      setRangeStart((s) => s || payload.period.month.start);
      setRangeEnd((e) => e || payload.period.month.end);
    }
  }, [payload]);

  const fetchDaily = useCallback(async (start, end) => {
    if (!start || !end) return;
    setChartLoading(true);
    try {
      const resp = await getApiCall(API_CALL_CENTRE_SPEND_ANALYTICS_DAILY, {
        start,
        end,
      });
      const d = resp?.data || resp;
      setRangeDaily(Array.isArray(d?.daily) ? d.daily : []);
    } catch (err) {
      console.error(err);
      toast.error(friendlyApiError(err));
    } finally {
      setChartLoading(false);
    }
  }, []);

  const resetRange = () => {
    const start = payload?.period?.month?.start || '';
    const end = payload?.period?.month?.end || '';
    setRangeStart(start);
    setRangeEnd(end);
    setRangeDaily(null);
  };

  const openDetail = async (row) => {
    if (!row?.id) return;
    setShowDetail(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const url = API_CALL_CENTRE_SPEND_ANALYTICS_DETAIL.replace(':id', row.id);
      const resp = await getApiCall(url, {});
      setDetail(resp?.data || resp);
    } catch (err) {
      console.error(err);
      toast.error(friendlyApiError(err));
      setShowDetail(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const dailyRaw = useMemo(() => payload?.chart?.daily_last_month || [], [payload]);

  const chartDaily = useMemo(
    () => (rangeDaily != null ? rangeDaily : dailyRaw),
    [rangeDaily, dailyRaw],
  );

  const chartData = useMemo(() => aggregateSpend(chartDaily, groupBy), [chartDaily, groupBy]);

  const detailPieData = useMemo(() => {
    const b = detail?.last_month?.breakdown || {};
    return [
      { name: 'Incoming', key: 'incoming', value: Number(b.incoming || 0) },
      { name: 'Outgoing', key: 'outgoing', value: Number(b.outgoing || 0) },
      { name: 'IVR', key: 'ivr', value: Number(b.ivr || 0) },
      { name: 'Others', key: 'others', value: Number(b.others || 0) },
    ].filter((d) => d.value > 0);
  }, [detail]);

  if (Object.keys(moduleOps).length > 0 && !canRead) {
    return (
      <div className="text-center p-4" style={{ marginTop: 50, fontSize: 16 }}>
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const totals = payload?.totals || {};
  const centres = payload?.call_centres || [];

  const kpis = [
    {
      key: 'spent_week',
      label: 'Spent (7 days)',
      value: formatRupeeAmount(totals.spent_last_week || 0),
      trend: totals.trend_spent_week,
      suffix: 'vs previous 7 days',
      icon: <FiCreditCard size={18} />,
      variant: 'violet',
      color: '#7c3aed',
      dataKey: 'spent',
    },
    {
      key: 'spent_month',
      label: 'Spent (30 days)',
      value: formatRupeeAmount(totals.spent_last_month || 0),
      trend: totals.trend_spent_month,
      suffix: 'vs previous 30 days',
      icon: <FiTrendingUp size={18} />,
      variant: 'green',
      color: '#16a34a',
      dataKey: 'spent',
    },
    {
      key: 'calls_week',
      label: 'Calls (7 days)',
      value: Number(totals.calls_last_week || 0).toLocaleString('en-IN'),
      trend: totals.trend_calls_week,
      suffix: 'vs previous 7 days',
      icon: <FiPhoneCall size={18} />,
      variant: 'orange',
      color: '#f59e0b',
      dataKey: 'calls',
    },
    {
      key: 'calls_month',
      label: 'Calls (30 days)',
      value: Number(totals.calls_last_month || 0).toLocaleString('en-IN'),
      trend: totals.trend_calls_month,
      suffix: 'vs previous 30 days',
      icon: <FiPhone size={18} />,
      variant: 'blue',
      color: '#3a57e8',
      dataKey: 'calls',
    },
  ];

  const summaryColumns: ColumnDef<any>[] = [
    { accessorKey: 'name', header: 'Call Center' },
    {
      id: 'did_count',
      header: 'DIDs',
      cell: ({ row }) => (
        <span
          className="cc-link"
          role="button"
          tabIndex={0}
          onClick={() => openDetail(row.original)}
          onKeyDown={(e) => e.key === 'Enter' && openDetail(row.original)}
          style={{ cursor: 'pointer', color: '#3a57e8' }}
        >
          {row.original.did_count ?? 0}
        </span>
      ),
    },
    {
      accessorKey: 'spent_last_week',
      header: 'Spent (7d)',
      cell: ({ row }) => (
        <div className="cc-cell">
          <span className="cc-cell__val">
            {formatRupeeAmount(row.original.spent_last_week || 0)}
          </span>
          <TrendMini value={row.original.trend_spent_week} />
        </div>
      ),
    },
    {
      accessorKey: 'spent_last_month',
      header: 'Spent (30d)',
      cell: ({ row }) => (
        <div className="cc-cell">
          <span className="cc-cell__val">
            {formatRupeeAmount(row.original.spent_last_month || 0)}
          </span>
          <TrendMini value={row.original.trend_spent_month} />
        </div>
      ),
    },
    {
      accessorKey: 'calls_last_week',
      header: 'Calls (7d)',
      cell: ({ row }) => (
        <div className="cc-cell">
          <span className="cc-cell__val">
            {Number(row.original.calls_last_week || 0).toLocaleString('en-IN')}
          </span>
          <TrendMini value={row.original.trend_calls_week} />
        </div>
      ),
    },
    {
      accessorKey: 'calls_last_month',
      header: 'Calls (30d)',
      cell: ({ row }) => (
        <div className="cc-cell">
          <span className="cc-cell__val">
            {Number(row.original.calls_last_month || 0).toLocaleString('en-IN')}
          </span>
          <TrendMini value={row.original.trend_calls_month} />
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openDetail(row.original);
          }}
          className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary/10 px-2.5 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
          aria-label="View details"
          title="View details"
        >
          <FiEye size={14} />
          View
        </button>
      ),
    },
  ];

  const analyticsBody = (
    <>
      <PageHeader
        title="Call Center Spendings"
        description="Voice credit usage across your call centers (last 7 / 30 days)."
      />
      <div className="cc-spend-analytics mt-4">
      <div className="row">
        <div className="col-12">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {kpis.map((kpi) => (
              <div
                key={kpi.key}
                className="rounded-lg border border-border/60 bg-card p-4 shadow-neu-raised"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-md"
                    style={{ background: `${kpi.color}1f`, color: kpi.color }}
                  >
                    {kpi.icon}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {kpi.label}
                    </div>
                    <div className="text-lg font-bold tabular-nums text-foreground">
                      {kpi.value}
                    </div>
                  </div>
                </div>
                <Sparkline
                  data={dailyRaw}
                  dataKey={kpi.dataKey}
                  color={kpi.color}
                  gradientId={`cc-spark-${kpi.key}`}
                />
                <TrendPill value={kpi.trend} suffix={kpi.suffix} />
              </div>
            ))}
          </div>

          <Card className="mt-4 p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <BarChart3 className="h-4 w-4" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Daily Spend Breakdown
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Voice credit usage split by call type
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <DateRangeFilter
                  compact
                  value={{ from: rangeStart, to: rangeEnd }}
                  onChange={({ from, to }) => {
                    if (from === rangeStart && to === rangeEnd) return;
                    setRangeStart(from);
                    setRangeEnd(to);
                    if (from && to) fetchDaily(from, to);
                  }}
                  onReset={rangeDaily != null ? resetRange : undefined}
                />
                <div className="ml-1 inline-flex h-9 items-center gap-1 rounded-full border border-border/60 bg-card p-1 shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]">
                  {GROUP_OPTIONS.map((o) => {
                    const active = groupBy === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setGroupBy(o.value)}
                        className={
                          'rounded-full px-3 py-1 text-xs font-medium transition-colors ' +
                          (active
                            ? 'bg-primary text-primary-foreground shadow-[0_2px_5px_rgba(0,0,0,0.15)]'
                            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')
                        }
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {chartLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading spend data…</div>
            ) : chartData.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No usage data in this period</div>
            ) : (
              <>
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={chartData}
                      margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                      barCategoryGap="20%"
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        fontSize={11}
                        minTickGap={12}
                        tick={{ fill: '#94a3b8' }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        fontSize={11}
                        width={48}
                        tick={{ fill: '#94a3b8' }}
                        tickFormatter={(v) => `₹${Math.round(Number(v) || 0)}`}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(148,163,184,0.08)' }}
                        content={<SpendTooltip />}
                      />
                      {SERIES.map((s, i) => (
                        <Bar
                          key={s.key}
                          dataKey={s.key}
                          stackId="a"
                          fill={s.color}
                          name={s.label}
                          maxBarSize={22}
                          radius={i === SERIES.length - 1 ? [3, 3, 0, 0] : undefined}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="cc-legend d-flex flex-wrap gap-3 mt-2">
                  {SERIES.map((s) => (
                    <span
                      className="cc-legend__item d-inline-flex align-items-center gap-1"
                      key={s.key}
                      style={{ fontSize: 12, color: '#475569' }}
                    >
                      <span
                        className="cc-legend__dot"
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: s.color,
                        }}
                      />
                      {s.label}
                    </span>
                  ))}
                </div>
              </>
            )}
          </Card>

          <div className="mt-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-foreground">
                Call Center Summary
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={fetchList}
                className="gap-1.5"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </Button>
            </div>

            <DataTable
              tableId="callcentres.spend-analytics"
              columns={summaryColumns}
              data={centres}
              emptyTitle="No call centers"
              emptyDescription="No call centers found."
              pageSize={10}
              disableExport
            />
          </div>
        </div>
      </div>

      <Dialog open={showDetail} onOpenChange={(next) => !next && setShowDetail(false)}>
        <DialogContent className="cc-spend-detail-modal max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <BarChart3 className="h-4 w-4" />
              </span>
              <span className="flex flex-col">
                <span>Spend Detail</span>
                {detail?.call_centre?.name && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {detail.call_centre.name}
                  </span>
                )}
              </span>
            </DialogTitle>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="py-4 text-center">
              <Loader />
            </div>
          ) : (
            <>
              <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  {
                    label: '7d spent',
                    value: formatRupeeAmount(detail.last_week?.spent_total || 0),
                    icon: <Wallet className="h-4 w-4" />,
                    tone: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
                  },
                  {
                    label: '30d spent',
                    value: formatRupeeAmount(detail.last_month?.spent_total || 0),
                    icon: <CalendarDays className="h-4 w-4" />,
                    tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
                  },
                  {
                    label: 'DIDs',
                    value: detail.call_centre?.did_count ?? 0,
                    icon: <Hash className="h-4 w-4" />,
                    tone: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
                  },
                  {
                    label: '30d calls',
                    value: Number(detail.last_month?.calls || 0).toLocaleString('en-IN'),
                    icon: <PhoneCall className="h-4 w-4" />,
                    tone: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="rounded-lg border border-border/60 bg-card p-3"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          'flex h-8 w-8 items-center justify-center rounded-md ' + s.tone
                        }
                      >
                        {s.icon}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          {s.label}
                        </div>
                        <div className="text-sm font-bold tabular-nums text-foreground">
                          {s.value}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Spend breakdown (30 days)
              </div>
              <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-12">
                <div className="md:col-span-5">
                  {detailPieData.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No spend in period
                    </div>
                  ) : (
                    <div style={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={detailPieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {detailPieData.map((entry) => (
                              <Cell
                                key={entry.key}
                                fill={PIE_COLORS[entry.key] || '#94a3b8'}
                              />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => formatRupeeAmount(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                <div className="md:col-span-7">
                  <div className="overflow-x-auto rounded-lg border border-border/60">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Type
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            7 days
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            30 days
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Calls (30d)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/60">
                        {[
                          ['Incoming', 'incoming'],
                          ['Outgoing', 'outgoing'],
                          ['IVR', 'ivr'],
                          ['Others', 'others'],
                        ].map(([label, key]) => (
                          <tr key={key}>
                            <td className="px-3 py-2 text-foreground">
                              <span className="inline-flex items-center gap-2">
                                <span
                                  className="inline-block h-2.5 w-2.5 rounded-full"
                                  style={{ background: PIE_COLORS[key] }}
                                />
                                {label}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-foreground">
                              {formatRupeeAmount(detail.last_week?.breakdown?.[key] || 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-foreground">
                              {formatRupeeAmount(detail.last_month?.breakdown?.[key] || 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                              {Number(
                                detail.last_month?.call_counts?.[key] || 0,
                              ).toLocaleString('en-IN')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Daily trend (30 days)
              </div>
              {(detail.chart?.daily_last_month || []).length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No daily data</div>
              ) : (
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={detail.chart.daily_last_month} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d) => {
                          const dt = new Date(`${d}T00:00:00`);
                          return Number.isNaN(dt.getTime())
                            ? d
                            : dt.toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                              });
                        }}
                        tickLine={false}
                        axisLine={false}
                        fontSize={11}
                        tick={{ fill: '#94a3b8' }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        fontSize={11}
                        tick={{ fill: '#94a3b8' }}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          formatRupeeAmount(value),
                          String(name).charAt(0).toUpperCase() + String(name).slice(1),
                        ]}
                      />
                      {SERIES.map((s, i) => (
                        <Bar
                          key={s.key}
                          dataKey={s.key}
                          stackId="a"
                          fill={s.color}
                          name={s.label}
                          maxBarSize={18}
                          radius={i === SERIES.length - 1 ? [3, 3, 0, 0] : undefined}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </>
  );

  if (embedded) return analyticsBody;
  return <FillPage>{analyticsBody}</FillPage>;
};

export default CallCentreSpendAnalytics;
